import type { Enrollment } from "@fistos/shared/api";
import { Check, Copy, Plus, QrCode, RefreshCw } from "lucide-solid";
import { createSignal, Match, Show, Switch } from "solid-js";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { api, errorText } from "@/lib/api";
import { now } from "@/lib/clock";

export function EnrollDialog() {
  const [open, setOpen] = createSignal(false);
  const [code, setCode] = createSignal<Enrollment | null>(null);
  const [error, setError] = createSignal("");
  const [copied, setCopied] = createSignal(false);

  const fetchCode = async () => {
    setCode(null);
    setError("");
    try {
      setCode(await api.enroll());
    } catch (e) {
      setError(errorText(e));
    }
  };
  const left = () => {
    const c = code();
    return c ? Math.max(0, Math.round((c.expiresAt - now()) / 1000)) : 0;
  };
  const copy = async () => {
    await navigator.clipboard.writeText(code()!.payload);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Dialog
      open={open()}
      onOpenChange={(o) => {
        setOpen(o);
        if (o) void fetchCode();
      }}
    >
      <DialogTrigger as={Button<"button">}>
        <Plus /> Enroll device
      </DialogTrigger>
      <DialogPortal>
        <DialogContent class="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Enroll a FistOS phone</DialogTitle>
            <DialogDescription>
              On the phone open <b>Settings → System → AI Agent</b>, choose <b>Enroll</b>, and scan this code. It works
              once.
            </DialogDescription>
          </DialogHeader>
          <Switch>
            <Match when={error()}>
              <p class="text-destructive text-sm">{error()}</p>
            </Match>
            <Match when={!code()}>
              <Skeleton class="mx-auto aspect-square w-64 rounded-lg" />
            </Match>
            <Match when={code()}>
              {(c) => (
                <div class="min-w-0 space-y-3">
                  <div class="relative mx-auto w-64">
                    {/* SVG generated by our own server from the payload (qrcode library). */}
                    <div
                      class="overflow-hidden rounded-lg border bg-white p-1 [&_svg]:h-auto [&_svg]:w-full"
                      classList={{ "opacity-15": left() === 0 }}
                      innerHTML={c().qrSvg}
                    />
                    <Show when={left() === 0}>
                      <div class="absolute inset-0 flex items-center justify-center">
                        <Button variant="outline" onClick={fetchCode}>
                          <RefreshCw /> New code
                        </Button>
                      </div>
                    </Show>
                  </div>
                  <div class="text-muted-foreground text-center text-xs tabular-nums">
                    <Show when={left() > 0} fallback="Expired">
                      Expires in {Math.floor(left() / 60)}:{String(left() % 60).padStart(2, "0")}
                    </Show>
                  </div>
                  <div class="bg-muted/50 flex items-center gap-2 rounded-md border p-2">
                    <QrCode class="text-muted-foreground size-4 shrink-0" />
                    <code class="min-w-0 flex-1 truncate text-xs">{c().payload}</code>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      onClick={copy}
                      aria-label="Copy enrollment code"
                      disabled={left() === 0}
                    >
                      <Show when={copied()} fallback={<Copy />}>
                        <Check />
                      </Show>
                    </Button>
                  </div>
                  <p class="text-muted-foreground text-xs">
                    Or paste the code on the phone. It carries the gateway address, the certificate pin and a one-time
                    token, nothing else.
                  </p>
                </div>
              )}
            </Match>
          </Switch>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
}
